import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import EmailVerificationBanner from './EmailVerificationBanner';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: '📊' },
  { path: '/availability', label: 'Availability', icon: '📅' },
  { path: '/games', label: 'Games', icon: '🎯' },
  { path: '/friends', label: 'Friends', icon: '👥' },
  { path: '/leagues', label: 'Leagues', icon: '🏆' },
  { path: '/links', label: 'Booking Links', icon: '🔗' },
  { path: '/my-profile', label: 'My Profile', icon: '👤' },
  { path: '/settings', label: 'Settings', icon: '⚙️' }
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu when navigating
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen">
      {/* Email Verification Banner */}
      <EmailVerificationBanner />

      {/* Header */}
      <header className="bg-[#0a0a18] border-b border-[#1a1a3a]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Hamburger button - mobile only */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 -ml-2 text-gray-400 hover:text-white transition-colors"
              aria-label="Open menu"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>
            <Link to={user?.profileSlug ? `/p/${user.profileSlug}` : '/dashboard'}>
              <img src="/images/dartsScheduler-trn.png" alt="Darts Scheduler" className="h-8" />
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400 hidden sm:inline">{user?.displayName}</span>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Slide-out Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Menu Panel */}
          <nav className="fixed left-0 top-0 bottom-0 w-72 bg-[#0a0a18] border-r border-[#1a1a3a] shadow-xl overflow-y-auto">
            {/* Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#1a1a3a]">
              <Link to={user?.profileSlug ? `/p/${user.profileSlug}` : '/dashboard'} onClick={() => setMobileMenuOpen(false)}>
                <img src="/images/dartsScheduler-trn.png" alt="Darts Scheduler" className="h-8" />
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                aria-label="Close menu"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* User Info */}
            <div className="px-4 py-3 border-b border-[#1a1a3a]">
              <p className="text-sm text-gray-400">Signed in as</p>
              <p className="text-white font-medium">{user?.displayName}</p>
            </div>

            {/* Nav Items */}
            <div className="py-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-4 text-base font-medium transition-colors ${
                      isActive
                        ? 'bg-[#1111d4]/20 text-[#1111d4] border-l-4 border-[#1111d4]'
                        : 'text-gray-300 hover:bg-[#1a1a3a] hover:text-white border-l-4 border-transparent'
                    }`
                  }
                >
                  <span className="text-xl">{item.icon}</span>
                  {item.label}
                </NavLink>
              ))}
            </div>

            {/* Logout in Menu */}
            <div className="border-t border-[#1a1a3a] p-4">
              <button
                onClick={handleLogout}
                className="w-full py-3 text-center text-gray-400 hover:text-white hover:bg-[#1a1a3a] rounded-lg transition-colors"
              >
                Logout
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Desktop Navigation - hidden on mobile */}
      <nav className="hidden md:block bg-[#0a0a18]/80 backdrop-blur-sm border-b border-[#1a1a3a]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-1 overflow-x-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? 'border-[#1111d4] text-[#1111d4]'
                      : 'border-transparent text-gray-400 hover:text-white hover:bg-[#1a1a3a]'
                  }`
                }
              >
                <span className="mr-2">{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
